import React, { useMemo, useState } from 'react'
import type { PlatinumArtifactData } from '../../agent'
import InlinePlatinumTable from './InlinePlatinumTable'
import CompositeTable from '../composite-table/CompositeTable'
import ProvenanceModal from '../composite-table/ProvenanceModal'
import type { CellProvenance, ProvenanceSidecar } from '../composite-table/provenance.generated'
import { makeChatDatasetLoader, makeChatScriptLoader } from '../../plugins/compositeTablePlugin'
import { API_BASE_URL } from '../../constants/system'

/**
 * Render Platinum table/chart JSON artifacts inside the artifact viewer/preview.
 *
 * A composite (compose_table) writes ordinary PlatinumData plus a top-level
 * `provenance` sidecar — the shape match in tryParsePlatinumJson picks it up
 * as a raw table, but InlinePlatinumTable expects colpc/rowpc it doesn't have,
 * so it rendered as an all-blank table. Route anything carrying `provenance`
 * to CompositeTable instead — the same component the live chat SSE rendering
 * uses (compositeTablePlugin.tsx) — with cell click opening the same
 * ProvenanceModal.
 */
function PlatinumArtifactRenderer({ data, sessionId }: { data: PlatinumArtifactData; sessionId: string }) {
  const [openProvenance, setOpenProvenance] = useState<CellProvenance | null>(null)
  const loadDataset = useMemo(() => makeChatDatasetLoader(sessionId, API_BASE_URL), [sessionId])
  const loadScript = useMemo(() => makeChatScriptLoader(sessionId, API_BASE_URL), [sessionId])
  const raw = data.platinumData as { provenance?: ProvenanceSidecar } | undefined
  const provenance = raw?.provenance

  if (provenance) {
    return (
      <>
        <CompositeTable
          data={data.platinumData as never}
          provenance={provenance}
          title={data.title}
          onCellClick={(_row, _col, prov) => setOpenProvenance(prov)}
        />
        <ProvenanceModal
          open={openProvenance !== null}
          onClose={() => setOpenProvenance(null)}
          provenance={openProvenance}
          // The receipt is the composite's, not the cell's, and this renderer
          // has the whole sidecar in hand.
          run={provenance.run}
          loadDataset={loadDataset}
          loadScript={loadScript}
        />
      </>
    )
  }

  return (
    <InlinePlatinumTable
      platinumData={data.platinumData}
      title={data.title}
      customer={data.customer}
      job={data.job}
      spec={data.spec}
      defaultViewMode={data.viewMode}
      defaultChartType={data.chartType}
    />
  )
}

/** Curried so each caller can bind its own sessionId to the ArtifactViewer/ArtifactPreviewDialog `renderPlatinumData` prop. */
export function makeRenderPlatinumData(sessionId: string) {
  return function renderPlatinumData(data: PlatinumArtifactData) {
    return <PlatinumArtifactRenderer data={data} sessionId={sessionId} />
  }
}
